import React from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { BoldTitle } from '../../CustomizedComponents/Texts';
import SectionWrapper from '../../CardRow/SectionWrapper';
import CustomTextField from '../../CustomizedComponents/CustomTextField';
import CustomNumberFormat from '../../CustomizedComponents/CustomNumberFormat';
import { purchaseMethodsEdit } from '../../../pages/ContractDetailPage/components/ContractEnum';
import { SelectorOptions } from '../../SelectorOptions/SelectorOptions';

const getOptions = (option) => {
  return option.map((item) => (
    <option key={item.id} value={item.value !== undefined ? item.value : item.id}>
      {item.name}
    </option>
  ));
};

const generateContractPeriodValues = (startYear = 2020, endYear = 2050) => {
  return Array.from({ length: endYear - startYear + 1 }, (_, i) => ({
    id: startYear + i,
    name: startYear + i,
  }));
};

const ContractBasicInfo = ({ contract, handlers, editable, isNew, register, errors, enums }) => {
  const {
    idExternal,
    contractPeriod,
    customerId,
    commodityId,
    currencyId,
    supplyProductId,
    isBeskAksAvailable,
    isSettlementAvailable,
    isSaleAvailable,
    isAutoPurchase,
    isBA,
    customersEnum,
    purchaseMethodId,
    frameContract,
    invoicingCurrencyId,
    kspotPlus,
    kspotMinus,
  } = contract;

  const {
    setIdExternal,
    setCustomerId,
    setCommodityId,
    setCurrencyId,
    setSupplyProductId,
    setContractPeriod,
    setIsBeskAksAvailable,
    setIsSettlementAvailable,
    setIsSaleAvailable,
    setIsAutoPurchase,
    setPurchaseMethodId,
    setFrameContract,
    setInvoicingCurrencyId,
    setKSpotPlus,
    setKSpotMinus,
  } = handlers;
  const { commodities, currencies, supplyProducts, purchaseMethods } = enums;
  const intl = useIntl();
  const dataMissing = intl.formatMessage({ id: 'app.data.missing' });
  const basicData = 'app.basicData';
  const currency = 'app.contract.currency';
  const product = 'app.contract.product';
  const commodityTitle = 'app.contract.commodity';
  const organizationTitle = 'app.organization';
  const isBeskAksAvailableTitle = 'app.contract.isBeskAksAvailable';
  const isSettlementAvailableTitle = 'app.contract.isSettlementAvailable';
  const isSaleAvailableTitle = 'app.contract.isSaleAvailable';
  const isAutoPurchaseTitle = 'app.contract.isAutoPurchase';
  const idExternalTitle = 'app.contract.idExternal';
  const period = 'app.contract.period';
  const purchaseMethodTitle = 'app.contract.purchaseMethod';
  const frameContractTitle = 'app.contracts.frameContract';
  const invoicingCurrencyTitle = 'app.contracts.invoicingCurrency';
  const kSpotPlusTitle = 'app.contracts.kSpotPlus';
  const kSpotMinusTitle = 'app.contracts.kSpotMinus';

  return (
    <>
      <SectionWrapper>
        <BoldTitle title={basicData} />
        <CustomTextField
          id="1"
          name={'customerId'}
          onChange={(event) => {
            setCustomerId(event.target.value);
          }}
          select
          title={organizationTitle}
          value={customerId}
          disabled={!isNew}
          SelectProps={{
            native: true,
          }}
          variant="outlined"
          inputRef={register({
            required: dataMissing,
          })}
          helperText={errors?.customerId?.message}
          error={!!errors.customerId}>
          <option value={null} />
          {getOptions(customersEnum)}
        </CustomTextField>

        <CustomTextField
          id="2"
          name="idExternal"
          onChange={(event) => {
            setIdExternal(event.target.value);
          }}
          title={idExternalTitle}
          value={idExternal}
          variant="outlined"
          disabled={!editable}
          inputRef={register({
            required: dataMissing,
          })}
          helperText={errors?.idExternal?.message}
          error={!!errors.idExternal}
        />

        <CustomTextField
          id="3"
          name="frameContract"
          onChange={(event) => {
            if (event.target.value.toString().length <= 20) {
              setFrameContract(event.target.value);
            }
          }}
          title={frameContractTitle}
          value={frameContract}
          variant="outlined"
          disabled={!editable}
          inputRef={register({
            maxLength: 20,
          })}
          helperText={errors?.frameContract?.message}
          error={!!errors.frameContract}
        />

        <CustomTextField
          id="4"
          name={'commodityId'}
          onChange={(event) => {
            setCommodityId(event.target.value);
          }}
          select
          title={commodityTitle}
          value={commodityId}
          disabled={!isNew}
          SelectProps={{
            native: true,
          }}
          variant="outlined"
          inputRef={register({
            required: dataMissing,
          })}
          helperText={errors?.commodityId?.message}
          error={!!errors.commodityId}>
          <option value={null} />
          {getOptions(commodities)}
        </CustomTextField>

        <CustomTextField
          id="5"
          name="contractPeriod"
          onChange={(event) => {
            setContractPeriod(event.target.value);
          }}
          select
          title={period}
          value={contractPeriod}
          variant="outlined"
          disabled={!isNew}
          SelectProps={{
            native: true,
          }}
          inputRef={register({
            required: dataMissing,
          })}
          helperText={errors?.contractPeriod?.message}
          error={!!errors.contractPeriod}>
          <option value={null} />
          {getOptions(generateContractPeriodValues())}
        </CustomTextField>

        <CustomTextField
          id="6"
          name="currencyId"
          onChange={(event) => {
            setCurrencyId(event.target.value);
          }}
          select
          title={currency}
          value={currencyId}
          disabled={!isNew}
          SelectProps={{
            native: true,
          }}
          variant="outlined"
          inputRef={register({
            required: dataMissing,
          })}
          helperText={errors?.currencyId?.message}
          error={!!errors.currencyId}>
          <option value={null} />
          {getOptions(currencies)}
        </CustomTextField>

        <CustomTextField
          id="7"
          name="invoicingCurrencyId"
          onChange={(event) => {
            setInvoicingCurrencyId(event.target.value);
          }}
          select
          title={invoicingCurrencyTitle}
          value={invoicingCurrencyId}
          disabled={!editable}
          SelectProps={{
            native: true,
          }}
          variant="outlined"
          inputRef={register({
            required: dataMissing,
          })}
          helperText={errors?.invoicingCurrencyId?.message}
          error={!!errors.invoicingCurrencyId}>
          <option value={null} />
          {getOptions(currencies)}
        </CustomTextField>

        <CustomTextField
          id="8"
          name="supplyProductId"
          onChange={(event) => {
            setSupplyProductId(event.target.value);
          }}
          select
          title={product}
          value={supplyProductId}
          disabled={!isNew}
          SelectProps={{
            native: true,
          }}
          variant="outlined"
          inputRef={register({
            required: dataMissing,
          })}
          helperText={errors?.supplyProductId?.message}
          error={!!errors.supplyProductId}>
          <option value={null} />
          {getOptions(supplyProducts)}
        </CustomTextField>

        <CustomTextField
          id="9"
          name="isSettlementAvailable"
          onChange={(event) => {
            setIsSettlementAvailable(event.target.value === 'true');
          }}
          select
          title={isSettlementAvailableTitle}
          value={isSettlementAvailable}
          disabled={!editable}
          SelectProps={{
            native: true,
          }}
          variant="outlined"
          inputRef={register({
            required: dataMissing,
          })}
          helperText={errors?.isSettlementAvailable?.message}
          error={!!errors.isSettlementAvailable}>
          <SelectorOptions options={isBA} translateOptions={true}></SelectorOptions>
        </CustomTextField>

        <CustomTextField
          id="10"
          name="isBeskAksAvailable"
          onChange={(event) => {
            setIsBeskAksAvailable(event.target.value === 'true');
          }}
          select
          title={isBeskAksAvailableTitle}
          value={isBeskAksAvailable}
          disabled={!editable}
          SelectProps={{
            native: true,
          }}
          variant="outlined"
          inputRef={register({
            required: dataMissing,
          })}
          helperText={errors?.isBeskAksAvailable?.message}
          error={!!errors.isBeskAksAvailable}>
          <SelectorOptions options={isBA} translateOptions={true}></SelectorOptions>
        </CustomTextField>

        <CustomTextField
          id="11"
          name="isSaleAvailable"
          onChange={(event) => {
            setIsSaleAvailable(event.target.value === 'true');
          }}
          select
          title={isSaleAvailableTitle}
          value={isSaleAvailable}
          disabled={!editable}
          SelectProps={{
            native: true,
          }}
          variant="outlined"
          inputRef={register({
            required: dataMissing,
          })}
          helperText={errors?.isSaleAvailable?.message}
          error={!!errors.isSaleAvailable}>
          <SelectorOptions options={isBA} translateOptions={true}></SelectorOptions>
        </CustomTextField>

        <CustomTextField
          id="12"
          name="isAutoPurchase"
          onChange={(event) => {
            setIsAutoPurchase(event.target.value === 'true');
          }}
          select
          title={isAutoPurchaseTitle}
          value={isAutoPurchase}
          disabled={!editable}
          SelectProps={{
            native: true,
          }}
          variant="outlined"
          inputRef={register({
            required: dataMissing,
          })}
          helperText={errors?.isAutoPurchase?.message}
          error={!!errors.isAutoPurchase}>
          <SelectorOptions options={isBA} translateOptions={true}></SelectorOptions>
        </CustomTextField>

        {!isNew && purchaseMethodId === 'PROFILE' ? (
          <CustomTextField
            id="13"
            name={'purchaseMethodId'}
            onChange={(event) => {
              setPurchaseMethodId(event.target.value);
            }}
            select
            title={purchaseMethodTitle}
            value={purchaseMethodId}
            SelectProps={{
              native: true,
            }}
            variant="outlined"
            inputRef={register({
              required: dataMissing,
            })}
            helperText={errors?.purchaseMethodId?.message}
            error={!!errors.purchaseMethodId}>
            <SelectorOptions options={purchaseMethodsEdit} translateOptions={true}></SelectorOptions>
          </CustomTextField>
        ) : (
          <CustomTextField
            id="14"
            name={'purchaseMethodId'}
            onChange={(event) => {
              setPurchaseMethodId(event.target.value);
            }}
            select
            title={purchaseMethodTitle}
            value={purchaseMethodId}
            disabled={!isNew}
            SelectProps={{
              native: true,
            }}
            variant="outlined"
            inputRef={register({
              required: dataMissing,
            })}
            helperText={errors?.purchaseMethodId?.message}
            error={!!errors.purchaseMethodId}>
            <option value={null} />

            {getOptions(purchaseMethods)}
          </CustomTextField>
        )}
        <CustomTextField
          id="15"
          name="kSpotPlus"
          onChange={(event) => {
            setKSpotPlus(event.target.value);
          }}
          title={kSpotPlusTitle}
          value={kspotPlus}
          disabled={!editable}
          InputProps={{
            inputComponent: CustomNumberFormat,
          }}
          variant="outlined"
          helperText={errors?.kspotPlus?.message}
          error={!!errors.kspotPlus}>
        </CustomTextField>
        <CustomTextField
          id="16"
          name="kSpotMinus"
          onChange={(event) => {
            setKSpotMinus(event.target.value);
          }}
          title={kSpotMinusTitle}
          value={kspotMinus}
          disabled={!editable}
          InputProps={{
            inputComponent: CustomNumberFormat,
          }}
          variant="outlined"
          helperText={errors?.kspotMinus?.message}
          error={!!errors.kspotMinus}>
        </CustomTextField>
      </SectionWrapper>
    </>
  );
};

ContractBasicInfo.propTypes = {
  contract: PropTypes.object,
  handlers: PropTypes.object,
  editable: PropTypes.bool,
  isNew: PropTypes.bool,
  register: PropTypes.any,
  errors: PropTypes.object,
  enums: PropTypes.any,
};
export default ContractBasicInfo;
